import { useEffect } from 'react'
import { OG_LOCALE, alternatesFor, urlFor, type Locale } from '@/lib/seo'

type Meta = {
  title?: string
  description?: string
  /** Unprefixed site path of the page (e.g. '/services'); drives canonical, og:url and hreflang. */
  path?: string
  locale: Locale
  robots?: string
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function setAlternates(path: string) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove())
  for (const { hreflang, href } of alternatesFor(path)) {
    const el = document.createElement('link')
    el.setAttribute('rel', 'alternate')
    el.setAttribute('hreflang', hreflang)
    el.setAttribute('href', href)
    document.head.appendChild(el)
  }
}

/**
 * Lightweight document-head manager for the SPA. Updates title, description,
 * canonical, hreflang alternates, robots and the Open Graph / Twitter tags
 * on route or language change. The same values are prerendered per route
 * and language at build time (see vite.config.ts) for crawlers that do not
 * run JS. The canonical is the page's own language URL (seo.ts `urlFor`).
 */
export function useDocumentMeta({ title, description, path, locale, robots }: Meta) {
  useEffect(() => {
    if (title) {
      document.title = title
      upsertMeta('property', 'og:title', title)
      upsertMeta('name', 'twitter:title', title)
    }
    if (description) {
      upsertMeta('name', 'description', description)
      upsertMeta('property', 'og:description', description)
      upsertMeta('name', 'twitter:description', description)
    }
    if (path) {
      const url = urlFor(path, locale)
      upsertCanonical(url)
      upsertMeta('property', 'og:url', url)
      setAlternates(path)
    }
    upsertMeta('property', 'og:locale', OG_LOCALE[locale])
    upsertMeta('property', 'og:locale:alternate', OG_LOCALE[locale === 'de' ? 'en' : 'de'])
    upsertMeta('name', 'robots', robots ?? 'index, follow')
  }, [title, description, path, locale, robots])
}
